import { defineStore } from 'pinia';
import { emitCreatorPlanLimitPrompt } from '@/utils/creatorPlanLimitPrompt';

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const apiUrl = (path) => `${apiProxyUrl || ''}/api/creator-webinars${path}`;

async function request(path, method = 'GET', body) {
  const response = await fetch(apiUrl(path), {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.message || `Webinar live request failed (${response.status})`);
    error.status = response.status;
    error.code = payload?.code;
    error.details = payload?.details;
    emitCreatorPlanLimitPrompt(error);
    throw error;
  }
  return payload;
}

export const useWebinarLiveStore = defineStore('webinarLive', {
  state: () => ({
    sessionsByEvent: {},
    allowancesByEvent: {},
    publishTokensByEvent: {},
    previewTokensByEvent: {},
    replaysByEvent: {},
    offersByEvent: {},
    moderatorAccessByEvent: {},
    moderatorLinksByEvent: {},
    moderatorBusy: false,
    moderatorError: '',
    busy: false,
    offerBusy: false,
    error: '',
    offerError: '',
  }),
  getters: {
    sessionForEvent: (state) => (eventId) => state.sessionsByEvent[String(eventId)] || null,
    allowanceForEvent: (state) => (eventId) => state.allowancesByEvent[String(eventId)] || null,
    publishTokenForEvent: (state) => (eventId) => state.publishTokensByEvent[String(eventId)] || '',
    previewTokenForEvent: (state) => (eventId) => state.previewTokensByEvent[String(eventId)] || '',
    replayForEvent: (state) => (eventId) => state.replaysByEvent[String(eventId)] || null,
    offerForEvent: (state) => (eventId) => state.offersByEvent[String(eventId)] || null,
    moderatorAccessForEvent: (state) => (eventId) => state.moderatorAccessByEvent[String(eventId)] || null,
    moderatorLinkForEvent: (state) => (eventId) => state.moderatorLinksByEvent[String(eventId)] || '',
  },
  actions: {
    setSession(eventId, session) {
      this.sessionsByEvent = { ...this.sessionsByEvent, [String(eventId)]: session || null };
    },
    setAllowance(eventId, allowance) {
      this.allowancesByEvent = { ...this.allowancesByEvent, [String(eventId)]: allowance || null };
    },
    async run(eventId, suffix, method = 'POST', body) {
      this.busy = true;
      this.error = '';
      try {
        const payload = await request(`/events/${encodeURIComponent(eventId)}/live/${suffix}`, method, body);
        this.setSession(eventId, payload.session);
        if (payload.allowance) this.setAllowance(eventId, payload.allowance);
        if (payload.publishToken) {
          this.publishTokensByEvent = { ...this.publishTokensByEvent, [String(eventId)]: payload.publishToken };
        }
        if (payload.previewToken) {
          this.previewTokensByEvent = { ...this.previewTokensByEvent, [String(eventId)]: payload.previewToken };
        }
        if (payload.recording) {
          this.replaysByEvent = { ...this.replaysByEvent, [String(eventId)]: payload.recording };
        }
        return payload;
      } catch (error) {
        this.error = error?.message || 'Webinar live request failed';
        throw error;
      } finally {
        this.busy = false;
      }
    },
    prepare(eventId, source) { return this.run(eventId, 'prepare', 'POST', { source }); },
    fetchStatus(eventId) { return this.run(eventId, 'status', 'GET'); },
    fetchPreview(eventId) { return this.run(eventId, 'preview', 'GET'); },
    markReady(eventId) { return this.run(eventId, 'ready'); },
    start(eventId) { return this.run(eventId, 'start'); },
    end(eventId) { return this.run(eventId, 'end'); },
    async heartbeat(eventId) {
      try {
        const payload = await request(`/events/${encodeURIComponent(eventId)}/live/heartbeat`, 'POST');
        this.setSession(eventId, payload.session);
        if (payload.allowance) this.setAllowance(eventId, payload.allowance);
        return payload;
      } catch (error) {
        if (error?.details?.session) this.setSession(eventId, error.details.session);
        if (error?.details?.allowance) this.setAllowance(eventId, error.details.allowance);
        throw error;
      }
    },
    async fetchReplay(eventId) {
      this.error = '';
      try {
        const payload = await request(`/events/${encodeURIComponent(eventId)}/live/replay`, 'GET');
        this.replaysByEvent = {
          ...this.replaysByEvent,
          [String(eventId)]: payload.replay || null,
        };
        return payload.replay || null;
      } catch (error) {
        this.error = error?.message || 'Webinar replay request failed';
        throw error;
      }
    },
    setOffer(eventId, offer) {
      this.offersByEvent = { ...this.offersByEvent, [String(eventId)]: offer || null };
    },
    async runOffer(eventId, suffix = '', method = 'GET', body) {
      this.offerBusy = true;
      this.offerError = '';
      try {
        const payload = await request(
          `/events/${encodeURIComponent(eventId)}/live/offer${suffix ? `/${suffix}` : ''}`,
          method,
          body,
        );
        this.setOffer(eventId, payload.offer);
        return payload.offer || null;
      } catch (error) {
        this.offerError = error?.message || 'Webinar offer request failed';
        throw error;
      } finally {
        this.offerBusy = false;
      }
    },
    fetchOffer(eventId) { return this.runOffer(eventId); },
    saveOffer(eventId, offer) { return this.runOffer(eventId, '', 'PUT', offer); },
    publishOffer(eventId, offer) { return this.runOffer(eventId, 'publish', 'POST', offer); },
    hideOffer(eventId) { return this.runOffer(eventId, 'hide', 'POST'); },
    setModeratorAccess(eventId, access, moderatorLink = '') {
      const key = String(eventId);
      this.moderatorAccessByEvent = { ...this.moderatorAccessByEvent, [key]: access || null };
      this.moderatorLinksByEvent = { ...this.moderatorLinksByEvent, [key]: moderatorLink || '' };
    },
    async runModerator(eventId, suffix = '', method = 'GET', body) {
      this.moderatorBusy = true;
      this.moderatorError = '';
      try {
        const payload = await request(
          `/events/${encodeURIComponent(eventId)}/live/moderator${suffix ? `/${suffix}` : ''}`,
          method,
          body,
        );
        this.setModeratorAccess(eventId, payload.access, payload.moderatorLink);
        return payload;
      } catch (error) {
        this.moderatorError = error?.message || 'Webinar moderator request failed';
        throw error;
      } finally {
        this.moderatorBusy = false;
      }
    },
    fetchModeratorAccess(eventId) { return this.runModerator(eventId); },
    updateModeratorAccess(eventId, profile) { return this.runModerator(eventId, '', 'PATCH', profile); },
    generateModeratorAccess(eventId, profile) { return this.runModerator(eventId, 'generate', 'POST', profile); },
    revokeModeratorAccess(eventId) { return this.runModerator(eventId, 'revoke', 'POST'); },
  },
});
